type CrispSessionDataValue = string | number | boolean | null | undefined;

type CrispUserPayload = {
  email?: string | null;
  name?: string | null;
};

declare global {
  interface Window {
    $crisp?: Array<unknown>;
    CRISP_WEBSITE_ID?: string;
  }
}

const CRISP_SCRIPT_ID = "workscope-crisp-script";
const CRISP_STORAGE_KEY = "workscope:crisp-enabled";
const CRISP_EVENT_NAME = "workscope:crisp-preference";
const CRISP_HIDDEN_CLASS = "workscope-crisp-hidden";
const CRISP_STYLE_ID = "workscope-crisp-style";
const CRISP_WEBSITE_ID =
  (import.meta.env.VITE_CRISP_WEBSITE_ID as string | undefined)?.trim() ||
  "7d2b8b88-ef0c-4dbe-83bc-5bd691c8e4ba";
let crispLoadStarted = false;

function canUseCrisp() {
  return typeof window !== "undefined";
}

function getCrispQueue() {
  if (!canUseCrisp()) return null;
  if (!Array.isArray(window.$crisp)) {
    window.$crisp = [];
  }
  return window.$crisp;
}

function ensureCrispStyle() {
  if (!canUseCrisp()) return;
  if (document.getElementById(CRISP_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = CRISP_STYLE_ID;
  style.textContent = `
    body.${CRISP_HIDDEN_CLASS} .crisp-client,
    body.${CRISP_HIDDEN_CLASS} [id^="crisp-"],
    body.${CRISP_HIDDEN_CLASS} iframe[src*="crisp.chat"] {
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(style);
}

function setCrispDomVisibility(visible: boolean) {
  if (!canUseCrisp()) return;
  ensureCrispStyle();
  document.body.classList.toggle(CRISP_HIDDEN_CLASS, !visible);
}

export function ensureCrispLoaded() {
  if (!canUseCrisp()) return;
  ensureCrispStyle();

  const existing = document.getElementById(CRISP_SCRIPT_ID);
  if (existing) return;
  if (crispLoadStarted) return;
  crispLoadStarted = true;

  if (!window.CRISP_WEBSITE_ID) {
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  }

  const script = document.createElement("script");
  script.id = CRISP_SCRIPT_ID;
  script.src = "https://client.crisp.chat/l.js";
  script.async = true;
  document.head.appendChild(script);
}

export function isCrispEnabled() {
  if (!canUseCrisp()) return false;
  return window.localStorage.getItem(CRISP_STORAGE_KEY) === "true";
}

export function setCrispEnabled(enabled: boolean) {
  if (!canUseCrisp()) return;
  window.localStorage.setItem(CRISP_STORAGE_KEY, enabled ? "true" : "false");
  window.dispatchEvent(new CustomEvent(CRISP_EVENT_NAME, { detail: { enabled } }));
}

export function onCrispPreferenceChange(callback: (enabled: boolean) => void) {
  if (!canUseCrisp()) return () => undefined;
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<{ enabled?: boolean }>;
    callback(Boolean(customEvent.detail?.enabled));
  };
  window.addEventListener(CRISP_EVENT_NAME, handler);
  return () => window.removeEventListener(CRISP_EVENT_NAME, handler);
}

export function openCrisp() {
  const crisp = getCrispQueue();
  if (!crisp) return;
  setCrispDomVisibility(true);
  crisp.push(["do", "chat:show"]);
  crisp.push(["do", "chat:open"]);
}

export function hideCrisp() {
  const crisp = getCrispQueue();
  setCrispDomVisibility(false);
  if (!crisp) return;
  crisp.push(["do", "chat:close"]);
  crisp.push(["do", "chat:hide"]);
}

export function showCrisp() {
  const crisp = getCrispQueue();
  setCrispDomVisibility(true);
  if (!crisp) return;
  crisp.push(["do", "chat:show"]);
}

export function setCrispUser({ email, name }: CrispUserPayload) {
  const crisp = getCrispQueue();
  if (!crisp) return;
  if (email) crisp.push(["set", "user:email", [email]]);
  if (name) crisp.push(["set", "user:nickname", [name]]);
}

export function setCrispSessionData(data: Record<string, CrispSessionDataValue>) {
  const crisp = getCrispQueue();
  if (!crisp) return;
  const payload = Object.entries(data)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => [key, String(value)]);
  if (!payload.length) return;
  crisp.push(["set", "session:data", [payload]]);
}

export function resetCrispSession() {
  const crisp = getCrispQueue();
  if (!crisp) return;
  crisp.push(["do", "session:reset"]);
}
